import React, { useEffect, useState } from "react";
import { Star, Check } from "lucide-react";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { useAgencyNavigate } from "../lib/agency";

const CATEGORIES = ["Hotel", "Tours", "Traslados", "Comida", "Soporte"];

export default function Feedback() {
  const navigate = useAgencyNavigate();
  const user = auth.current();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [cats, setCats] = useState([]);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [services, setServices] = useState(null); // null = cargando
  const [serviceId, setServiceId] = useState("");

  // Único lugar de la app que navegaba durante el render en vez de en un
  // efecto — React no garantiza que un side-effect (redirigir) sea seguro
  // ahí; el resto de las pantallas ya usan este patrón.
  useEffect(() => { if (!user) navigate("/login"); }, []);

  useEffect(() => {
    if (!user) return;
    api.get(`/itineraries/${user.booking_id}`).then(({ data }) => {
      // Solo se puede calificar un servicio que ya empezó — no tiene
      // sentido pedir feedback de algo que todavía no pasa.
      const eligible = data.filter((it) => it.status !== "pendiente");
      setServices(eligible);
      if (eligible.length) setServiceId(eligible[0].id);
    }).catch(() => setServices([]));
  }, [user?.booking_id]);

  if (!user) return null;

  const toggleCat = (c) =>
    setCats((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]);

  const submit = async () => {
    setErr("");
    if (!serviceId) { setErr("Selecciona a qué servicio corresponde tu feedback."); return; }
    if (rating < 1) { setErr("Selecciona una calificación."); return; }
    try {
      await api.post("/feedback", {
        booking_id: user.booking_id,
        service_id: serviceId,
        rating, comment, categories: cats,
      });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al enviar");
    }
  };

  return (
    <Layout>
      <section className="max-w-2xl mx-auto px-6 md:px-10 py-12">
        <div className="mb-10 fade-up">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Feedback Post-Viaje</p>
          <h1 className="font-display text-4xl font-light tracking-tight">
            ¿Cómo estuvo tu <span className="text-[var(--brand-cyan,#FF85AC)]">experiencia</span>?
          </h1>
        </div>

        {sent ? (
          <div className="glass rounded-3xl p-10 text-center border border-[var(--brand-cyan,#FF85AC)]/30 fade-up" data-testid="feedback-sent">
            <div className="inline-flex w-14 h-14 rounded-full bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/40 items-center justify-center mb-5">
              <Check className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" />
            </div>
            <h2 className="font-display text-2xl mb-2">¡Gracias!</h2>
            <p className="text-neutral-400">Tu feedback nos ayuda a mejorar cada viaje.</p>
          </div>
        ) : services === null ? (
          <div className="glass rounded-3xl p-10 text-center text-neutral-500">Cargando…</div>
        ) : services.length === 0 ? (
          <div className="glass rounded-3xl p-10 text-center border border-white/10" data-testid="feedback-unavailable">
            <p className="text-neutral-400">Tu feedback estará disponible aquí en cuanto comience algún servicio de tu viaje.</p>
          </div>
        ) : (
          <div className="glass rounded-3xl p-8 border border-white/10 space-y-7">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">¿Sobre qué servicio es tu feedback?</label>
              <select
                value={serviceId}
                onChange={(e) => setServiceId(e.target.value)}
                data-testid="feedback-service-select"
                className="w-full bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-3 py-2.5 text-white"
              >
                {services.map((s) => (
                  <option key={s.id} value={s.id}>{s.title} — {s.start_date}</option>
                ))}
              </select>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Calificación</p>
              <div className="flex gap-2" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    data-testid={`star-${n}`}
                  >
                    <Star
                      className={`w-9 h-9 transition-all ${
                        (hover || rating) >= n ? "fill-[var(--brand-cyan,#FF85AC)] text-[var(--brand-cyan,#FF85AC)]" : "text-neutral-700"
                      }`}
                      strokeWidth={1.4}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">Aspectos destacados</p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => toggleCat(c)}
                    data-testid={`cat-${c}`}
                    className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] border transition-all ${
                      cats.includes(c)
                        ? "bg-[var(--brand-cyan,#FF85AC)] text-black border-[var(--brand-cyan,#FF85AC)]"
                        : "border-white/15 text-neutral-400 hover:border-[var(--brand-cyan,#FF85AC)]/50"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Comentario</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                data-testid="comment-input"
                className="w-full bg-black/40 rounded-2xl p-4 text-sm text-white border border-white/10 focus:border-[var(--brand-cyan,#FF85AC)]/50 outline-none transition-colors"
                placeholder="Cuéntanos lo mejor de tu viaje…"
              />
            </div>

            {err && <p className="text-sm text-[#FF3B30]" data-testid="feedback-error">{err}</p>}

            <button
              onClick={submit}
              data-testid="submit-feedback-btn"
              className="w-full bg-[var(--brand-cyan,#FF85AC)] text-black py-3.5 rounded-full font-semibold hover:bg-[#FF9DC0] cyan-glow"
            >
              Enviar Feedback
            </button>
          </div>
        )}
      </section>
    </Layout>
  );
}
